import React from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Printer, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import UserDisplay from "@/components/shared/UserDisplay";

import PrintDialog from "@/components/shared/PrintDialog";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Badge } from "@/components/ui/badge";
import { ReceiptStatus, getStatusConfig } from "@/config/status-registry";
import { EntityLink } from "@/components/shared/EntityLink";
import { DetailField as F } from "@/components/shared/DetailField";

const PAYMENT_METHOD_MAP: Record<string, string> = {
  TIEN_MAT: "Tiền mặt",
  CHUYEN_KHOAN: "Chuyển khoản",
  CAN_TRU: "Cấn trừ",
  // Mã cũ (máy chủ đã quy về mã chuẩn khi ghi; bản ghi cũ vẫn hiện nhãn tiếng Việt)
  CASH: "Tiền mặt",
  BANK_TRANSFER: "Chuyển khoản",
  CONG_NO: "Cấn trừ",
};

const STATUS_MAP = ReceiptStatus;

const TYPE_LABEL: Record<string, string> = {
  INCOME: "Phiếu Thu",
  EXPENSE: "Phiếu Chi",
};


export default function ReceiptViewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { receiptId } = useParams({ strict: false }) as any;
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [isPrinting, setIsPrinting] = React.useState(false);

  const { data: receipt, isLoading } = useQuery({
    queryKey: ["receipt", receiptId],
    queryFn: () => trpcClient.receipts.getById.query({ id: receiptId }),
    enabled: !!receiptId,
  });

  const handleDelete = async () => {
    try {
      await trpcClient.receipts.delete.mutate({ id: receiptId });

      queryClient.invalidateQueries({ queryKey: ["receipts"] });
      navigate({ to: "/finance/receipts" });
    } catch (error: any) {
      toast.error(error?.message || "Có lỗi xảy ra khi xoá");
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 w-full mx-auto space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <div className="bg-white p-6 rounded-xl border border-slate-200">
          <Skeleton className="h-[200px] w-full" />
        </div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-slate-500">
        <p>Không tìm thấy thông tin phiếu thu</p>
        <Button
          variant="link"
          onClick={() => navigate({ to: "/finance/receipts" })}
        >
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  const pm = PAYMENT_METHOD_MAP[receipt.paymentMethod] || receipt.paymentMethod;
  const st = STATUS_MAP[receipt.status] || STATUS_MAP.DRAFT;

  return (
    <StandardDetailLayout
      breadcrumbs={[{ label: "Tài chính", to: "/finance/receipts" }]}
      title={receipt.receiptId}
      entityId={receipt.id}
      entityType="receipt"
      timestamps={{
        created: receipt.createdAt,
        updated: receipt.updatedAt,
      }}
      status={
        <Badge variant="outline" className={st.color}>
          {st.label}
        </Badge>
      }
      actions={
        <div className="flex gap-1.5 flex-wrap">
          {receipt.customerId && (
            <Button
              variant="outline"
              size="sm"
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              onClick={() =>
                navigate({
                  to: `/finance/ar/ledger/${receipt.customerId}` as any,
                })
              }
              title="Xem sổ cái công nợ khách hàng"
            >
              <BookOpen className="mr-1.5 h-3.5 w-3.5" /> Sổ cái
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPrinting(true)}
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" /> In phiếu
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() =>
              navigate({ to: `/finance/receipts/${receiptId}/edit` })
            }
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Sửa
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-rose-600 border-rose-200 hover:bg-rose-50"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xoá
          </Button>
        </div>
      }
      onBack={() => navigate({ to: "/finance/receipts" })}
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xác nhận xoá phiếu thu?"
        description={`Bạn có chắc muốn xoá phiếu thu ${receipt.receiptId}? Hành động này không thể hoàn tác.`}
        confirmText="Xoá phiếu thu"
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      <div className="space-y-4 w-full mx-auto">
        {/* ═══ 1. THÔNG TIN CHUNG (Dense Layout) ═══ */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
            <div className="col-span-1 md:col-span-2">
              <div className="flex gap-2 min-w-0 items-start">
                <span className="text-slate-500 text-xs shrink-0 w-24 pt-[3px]">
                  Khách hàng
                </span>
                <div className="min-w-0 flex-1 leading-snug">
                  {receipt.customer ? (
                    <EntityLink
                      entityType="customer"
                      id={receipt.customerId || receipt.customer.id}
                      code={receipt.customer.code}
                      label={receipt.customer.name}
                    />
                  ) : (
                    <span className="text-slate-300 font-normal">—</span>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-1">
              <F label="Mã phiếu" value={receipt.receiptId} mono />
            </div>
            <div className="col-span-1">
              <F label="Số tiền" value={formatMoney(receipt.amount)} hl />
            </div>

            <div className="col-span-1">
              <F
                label="Ngày thu"
                value={
                  receipt.receiptDate
                    ? new Date(receipt.receiptDate).toLocaleDateString("vi-VN")
                    : "—"
                }
              />
            </div>
            <div className="col-span-1">
              <F label="Phương thức TT" value={pm} />
            </div>
            <div className="col-span-1">
              <F label="Số tham chiếu" value={receipt.referenceNumber || "—"} />
            </div>
            <div className="col-span-1">
              <F label="Loại phiếu" value={TYPE_LABEL[receipt.type] || "—"} />
            </div>

            {receipt.notes && (
              <div className="col-span-1 md:col-span-4 pt-1.5 mt-1.5 border-t border-slate-100">
                <F label="Ghi chú" value={receipt.notes} />
              </div>
            )}
          </div>
        </div>

        <PrintDialog
          open={isPrinting}
          onClose={() => setIsPrinting(false)}
          type="receipt"
          data={receipt}
        />
      </div>
    </StandardDetailLayout>
  );
}
